import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';

import '../styles/PortfolioDetail.css'
import portfolioData from '../data/Portfolio.json';
import HondaCover from '../images/portfolio/covers/honda-cover.jpg';
import FancyFeastCover from '../images/portfolio/covers/fancy-feast-cover.jpg';
import WabtecCover from '../images/portfolio/covers/wabtec-cover.png';
import HDpianoCover from '../images/portfolio/covers/hdpiano-cover.png';
import WalmartCover from '../images/portfolio/covers/walmart-cover.png';
import LittlePassportsCover from '../images/portfolio/covers/little-passports-cover.png';
import RelatedPortfolioCarousel from './RelatedPortfolioItems';
import { getProject } from '../api/strapi';

function PortfolioDetail() {
  const { id } = useParams(); // Extract dynamic id from the URL
  const navigate = useNavigate();

  // Helper to find local fallback item by numeric id, index, documentId, or title match
  const findFallback = (searchId) => {
    const numId = parseInt(searchId, 10);
    return portfolioData.find((item, idx) => 
      item.id === numId || 
      String(idx + 1) === String(searchId) ||
      (item.title && item.title.toLowerCase().includes(String(searchId).toLowerCase()))
    );
  };

  const [project, setProject] = useState(() => findFallback(id));

  useEffect(() => {
    getProject(id)
      .then((data) => {
        if (data) {
          setProject(data);
        } else {
          setProject(findFallback(id));
        }
      })
      .catch((err) => {
        console.error("Failed to load project details from Strapi, using local fallback:", err);
        setProject(findFallback(id));
      });
  }, [id]);

  if (!project) {
    return (
      <div className="container py-5 text-center">
        <h3>Project not found.</h3>
        <button 
          className="btn btn-outline-dark rounded-pill mt-3 px-4 py-2"
          onClick={() => navigate('/')}
        >
          &larr; Back to Home
        </button>
      </div>
    );
  }

  // Dedicated Brand Media Mappers by Project Title
  const getBrandHeaderImage = (projectTitle, mediaField) => {
    const title = (projectTitle || '').toLowerCase();
    if (title.includes('honda')) return HondaCover;
    if (title.includes('fancy feast') || title.includes('purina')) return FancyFeastCover;
    if (title.includes('wabtec')) return WabtecCover;
    if (title.includes('hdpiano')) return HDpianoCover;
    if (title.includes('walmart')) return WalmartCover;
    if (title.includes('little passports')) return LittlePassportsCover;

    const isLocal = typeof window !== 'undefined' && (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1');

    if (isLocal && typeof mediaField === 'object' && mediaField?.url) {
      return `http://localhost:1337${mediaField.url}`;
    }
    return HondaCover;
  };

  const getBrandAddMedia1 = (projectTitle, mediaField) => {
    const title = (projectTitle || '').toLowerCase();
    if (title.includes('honda')) return require('../images/portfolio/transportation-company/1.jpg');
    if (title.includes('fancy feast') || title.includes('purina')) return require('../images/portfolio/nestle-purina/1.jpg');
    if (title.includes('wabtec')) return require('../images/portfolio/bcg/1.jpg');
    if (title.includes('hdpiano')) return require('../images/portfolio/hdpiano/1.jpg');
    if (title.includes('walmart')) return require('../images/portfolio/walmart/1.jpg');
    if (title.includes('little passports')) return require('../images/portfolio/artful-crib/1.jpg');

    const isLocal = typeof window !== 'undefined' && (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1');

    if (isLocal && typeof mediaField === 'object' && mediaField?.url) {
      return `http://localhost:1337${mediaField.url}`;
    }
    return require('../images/portfolio/transportation-company/1.jpg');
  };

  const getBrandAddMedia2 = (projectTitle, mediaField) => {
    const title = (projectTitle || '').toLowerCase();
    if (title.includes('honda')) return require('../images/portfolio/transportation-company/2.jpg');
    if (title.includes('fancy feast') || title.includes('purina')) return require('../images/portfolio/nestle-purina/2.jpg');
    if (title.includes('wabtec')) return require('../images/portfolio/bcg/2.jpg');
    if (title.includes('hdpiano')) return require('../images/portfolio/hdpiano/2.jpg');
    if (title.includes('walmart')) return require('../images/portfolio/walmart/2.jpg');
    if (title.includes('little passports')) return require('../images/portfolio/artful-crib/2.jpg');

    const isLocal = typeof window !== 'undefined' && (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1');

    if (isLocal && typeof mediaField === 'object' && mediaField?.url) {
      return `http://localhost:1337${mediaField.url}`;
    }
    return require('../images/portfolio/transportation-company/2.jpg');
  };

  const headerImage = getBrandHeaderImage(project.title, project.header_image);
  const img1 = getBrandAddMedia1(project.title, project.additionalMedia1);
  const img2 = getBrandAddMedia2(project.title, project.additionalMedia2);

  // Normalizing problem, process, tags arrays between Strapi and JSON
  const tags = project.overview_tags || [];
  
  const problemText = Array.isArray(project.problem) 
    ? project.problem.join('\n') 
    : (project.problem || '');
    
  const discovery = project.process_discovery || (project.process && project.process.discovery_and_research) || [];
  const design = project.process_design || (project.process && project.process.design_and_prototyping) || [];
  const testing = project.process_testing || (project.process && project.process.testing_and_iteration) || [];
  
  const solutionList = project.solution || [];
  const techList = project.technologies || [];
  const resultsList = project.results || [];

  return (
    <div>
        <div className="portfolio-detail-header">
            <div className="container text-start mb-4">
                <button 
                  className="btn back-to-projects-btn rounded-pill px-4 py-2"
                  onClick={() => navigate('/portfolio')}
                >
                  &larr; Back to Projects
                </button>
            </div>
            <div className="container">
                <h5 className="project-subtitle">{project.subtitle}</h5> 
                <h1 className="project-detail-title">{project.title}</h1>
            </div>
            <div className="container mt-4">
                <img className="hero-image" src={headerImage} alt={project.title} />
            </div>
        </div>
        <div className="portfolio-detail-content">
            <div className="container">
                <div className="row">
                    {/* Overview */}
                    <div className="col-lg-6">
                        <div className="row my-5">
                            <h5>Overview</h5>
                            <h2>{project.overview_title}</h2>
                            <ul className="role-tags">
                                {tags.map((tag, index) => (
                                    <li className="role-tag" key={index}>{tag}</li>
                                ))}
                            </ul>
                        </div>
                    </div>
                    <div className="col-lg-6">
                        {/* Overview */}
                        <div className="row my-5">
                            <p>{project.overview}</p>
                        </div>
                        {/* Problem Statement */}
                        <div className="row my-5">
                            <h5>Problem Statement</h5>
                            <p style={{ whiteSpace: 'pre-line' }}>{problemText}</p>
                        </div>
                    </div>
                </div>
                {/* The Process */}
                <div className="row">
                    {/* Process */}
                    <div className="col-lg-6 pr-4">
                        <div className="row my-5">
                            <h5>The Process</h5>
                            <h2>{project.process_subtitle}</h2>
                        </div>
                        <div className="row">
                            <img className="portfolio-detail-img" src={img1} alt="" />
                        </div>
                    </div>
                    <div className="col-lg-6">
                        {/* Discovery & Research */}
                        <div className="row my-5">
                            <h5>Discovery & Research</h5>
                            <ul>
                                {discovery.map((item, idx) => (
                                    <li key={idx}>{item}</li>
                                ))}
                            </ul>
                        </div>
                        {/* Design & Prototyping */}
                        <div className="row my-5">
                            <h5>Design & Prototyping</h5>
                            <ul>
                                {design.map((item, idx) => (
                                    <li key={idx}>{item}</li>
                                ))}
                            </ul>
                        </div>
                        {/* Testing & Iteration */}
                        <div className="row my-5">
                            <h5>Testing & Iteration</h5>
                            <ul>
                                {testing.map((item, idx) => (
                                    <li key={idx}>{item}</li>
                                ))}
                            </ul>
                        </div>
                    </div>
                </div>
                {/* The Solution */}
                <div className="row">
                    {/* Solution */}
                    <div className="col-lg-6 pr-4">
                        <div className="row my-5">
                            <h5>The Solution</h5>
                            <h2>{project.solution_subtitle}</h2>
                        </div>
                        <div className="row">
                            <img className="portfolio-detail-img" src={img2} alt="" />
                        </div>
                    </div>
                    <div className="col-lg-6">
                        {/* Bullet Points */}
                        <div className="row my-5">
                            <h5>Features</h5>
                            <ul>
                                {solutionList.map((sol, index) => (
                                    <li key={index}>{sol}</li>
                                ))}
                            </ul>
                        </div>
                        <div className="row my-5">
                            <h5>Technology Stack</h5>
                            <ul className="role-tags">
                                {techList.map((tech, index) => (
                                    <li className="role-tag" key={index}>{tech}</li>
                                ))}
                            </ul>
                        </div>
                        <div className="row my-5">
                        <h5>Results</h5>
                            <ul>
                                {resultsList.map((result, index) => (
                                    <li key={index}>{result}</li>
                                ))}
                            </ul>
                        </div>
                        <div className="row my-5">
                            <h5>Conclusion</h5>
                            <p style={{ whiteSpace: 'pre-line' }}>{project.conclusion}</p>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    <RelatedPortfolioCarousel />
    </div>
  );
}

export default PortfolioDetail;
